import './ApplicationGroupDependencyGraphResourceDetail.scss';

import {
    ApplicationDependencyService, ApplicationGroupingService, ApplicationServerService, DatabaseApplicationService,
} from '@amzn/awscat-portfolio-assessment-service-client';
import {
    AppDependency, ApplicationGroup, ApplicationToServer, DatabaseApplication, GeneralDependencyType, GroupDetailsUpdate,
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, ExpandableSection, Form, FormField, Header, Icon, Input, Modal, NonCancelableCustomEvent,
    RadioGroup, RadioGroupProps, SpaceBetween, Toggle, ToggleProps,
} from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import {
    changeAppDatabaseMappingStatus, changeAppServerMappingStatus, changeDependencyStatus, decideDetailedMappingStatus, findApplicationDatabaseMapping,
    findApplicationServerMapping, statusDiff,
} from './ApplicationGroupHelper';
import { AppDatabaseMappingToUpdate,AppServerMappingToUpdate, DEPENDENCY_STATUS, DependencyDetail, isInfrastructureGroup } from './constants';

type Props = {
    selectedResource: DependencyDetail | null;
    groupDetail: ApplicationGroup | null;
    dependencies: AppDependency[] | null;
    onGroupDetailUpdate: (updatedGroup: ApplicationGroup) => void;
    onResourceHideToggle: (resource: DependencyDetail, hidden: boolean) => void;
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
} & FlashContextInterface;

const applicationGroupingService = new ApplicationGroupingService();
const applicationDependencyService = new ApplicationDependencyService();
const applicationServerService = new ApplicationServerService();
const databaseApplicationService = new DatabaseApplicationService();

function ApplicationGroupDependencyGraphResourceDetail(
    { selectedResource, dependencies, groupDetail, onGroupDetailUpdate, onResourceHideToggle, appServerMappings, appDatabaseMappings, flash }: Props
): JSX.Element | null {

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId || '';

    const history = useHistory();

    const [ groupDetailModalVisible, setGroupDetailModalVisible ] = useState(false);
    const [ groupDetailLoading, setGroupDetailLoading ] = useState(false);
    const [ groupDetailsToUpdate, setGroupDetailsToUpdate ] = useState<ApplicationGroup | null>(null);
    const [ removeAppVisible, setRemoveAppVisible ] = useState(false);
    const [ serverDependenciesToManage, setServerDependenciesToManage ] = useState<Record<string, boolean>>({});
    const [ databaseDependenciesToManage, setDatabaseDependenciesToManage ] = useState<Record<string, boolean>>({});
    const [ loading, setLoading ] = useState(false);

    const isMounted = useIsMounted();

    useEffect(() => {
        setGroupDetailsToUpdate(groupDetail);
    }, [ groupDetail ]);

    useEffect(() => {
        setRemoveAppVisible(false);
    }, [ selectedResource ]);

    const header = (title: string, onEditClick?: () => void) => (
        <Box padding={{bottom: 'l', horizontal: 'm', top: 'm'}} display='inline-block'>
            <Box display='inline-block'>
                <Header>{title}</Header>
            </Box>
            {onEditClick ? (<Box display='inline-block'><Button variant='icon' iconName='edit' onClick={onEditClick} /> </Box>) : null}
        </Box>
    );

    const onGroupDetailDismiss = () => {
        setGroupDetailModalVisible(false);
        setGroupDetailsToUpdate(groupDetail);
    };

    const openGroupDetailModal = () => {
        setGroupDetailModalVisible(true);
    };

    const onGroupUpdateClick = async () => {
        setGroupDetailLoading(true);
        try {
            if (groupDetail && groupDetailsToUpdate) {
                if (!groupDetail.locked && !groupDetailsToUpdate.locked) {
                    return;
                } else {
                    const limitedDetailsToUpdate: GroupDetailsUpdate = {
                        portfolioId: groupDetailsToUpdate.portfolioId,
                        name: groupDetailsToUpdate.name,
                        locked: groupDetailsToUpdate.locked,
                        applicationGroupId: groupDetailsToUpdate.applicationGroupId,
                    };
                    await applicationGroupingService.update(limitedDetailsToUpdate);
                    onGroupDetailUpdate({...groupDetail, locked: limitedDetailsToUpdate.locked, name: limitedDetailsToUpdate.name});
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_GROUP_INFO);
            }
        } finally {
            if (isMounted()) {
                setGroupDetailLoading(false);
                setGroupDetailModalVisible(false);
            }
        }
    };

    const getStatus = (group: ApplicationGroup) => group.locked ? constants.CLOSE : constants.OPEN;

    const statusOptions: RadioGroupProps.RadioButtonDefinition[] = [
        {
            value: constants.CLOSE,
            label: constants.CLOSE,
        },
        {
            value: constants.OPEN,
            label: constants.OPEN,
        },
    ];

    const onStatusChange = ({ detail }: NonCancelableCustomEvent<RadioGroupProps.ChangeDetail>) => {
        const locked = detail.value === constants.CLOSE ? true : false;
        if (groupDetailsToUpdate) {
            setGroupDetailsToUpdate({...groupDetailsToUpdate, locked});
        }
    };

    const editGroupDetailModal = groupDetailsToUpdate && (
        <Modal
            visible={groupDetailModalVisible}
            header={constants.GROUP_DETAILS}
            onDismiss={onGroupDetailDismiss}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='m'>
                        <Button variant='link' disabled={groupDetailLoading} onClick={onGroupDetailDismiss}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button
                            variant='primary'
                            disabled={groupDetailsToUpdate.name === '' || isInfrastructureGroup(groupDetailsToUpdate.applicationGroupId)}
                            loading={groupDetailLoading}
                            onClick={onGroupUpdateClick}
                        >
                            {constants.SAVE_BUTTON_TEXT}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <Form>
                <SpaceBetween size='l'>
                    <FormField label={constants.STATUS}>
                        <RadioGroup value={getStatus(groupDetailsToUpdate)} items={statusOptions} onChange={onStatusChange} />
                    </FormField>
                    <FormField label={constants.GROUP_NAME} errorText={groupDetailsToUpdate.name === '' ? constants.GROUP_NAME_ERROR : ''}>
                        <Input
                            disabled={!(groupDetailsToUpdate.locked)}
                            value={groupDetailsToUpdate.name}
                            onChange={({detail}) => setGroupDetailsToUpdate({...groupDetailsToUpdate, name: detail.value})}
                        />
                    </FormField>
                </SpaceBetween>
            </Form>
        </Modal>
    );

    const buildGroupDetail = (group: ApplicationGroup): JSX.Element => (
        <Box>
            {header(group.name, openGroupDetailModal)}
            <table className='group-summary-table'>
                <tbody>
                    <tr>
                        <td>{constants.STATUS}: </td>
                        <td>{group.locked ?
                            (<p><Icon variant='success' name='lock-private'/> {constants.CLOSE}</p>) :
                            (<p><Icon variant='warning' name='unlocked' /> {constants.OPEN}</p>)
                        }</td>
                    </tr>
                    <tr>
                        <td>{constants.RANKING}: </td>
                        <td>{group.ranking ?? '-'}</td>
                    </tr>
                </tbody>
            </table>
            <table className='group-summary-composition-table'>
                <tbody>
                    <tr>
                        <td>
                            <Box textAlign='center' fontSize='heading-l'>{group.totalApplications}</Box>
                        </td>
                        <td>
                            <Box textAlign='center' fontSize='heading-l'>{group.totalServers}</Box>
                        </td>
                        <td>
                            <Box textAlign='center' fontSize='heading-l'>{group.totalDatabases}</Box>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <Box textAlign='center' fontSize='heading-xs'>{constants.APPLICATIONS}</Box>
                        </td>
                        <td>
                            <Box textAlign='center' fontSize='heading-xs'>{constants.SERVERS}</Box>
                        </td>
                        <td>
                            <Box textAlign='center' fontSize='heading-xs'>{constants.DATABASES}</Box>
                        </td>
                    </tr>
                </tbody>
            </table>
            {editGroupDetailModal}
            <Box padding={{left: 'l'}}>
                <ExpandableSection variant='footer' defaultExpanded header={constants.HIDDEN_APPLICATION_S}>
                    <Box padding={{horizontal: 'l'}}></Box>
                </ExpandableSection>
                <ExpandableSection variant='footer' defaultExpanded header={constants.HIDDEN_SERVERS_AND_DATABASES}>
                    <Box padding={{left: 'l'}}>
                    </Box>
                </ExpandableSection>
            </Box>
        </Box>
    );

    const toggleResourceHide = () => {
        const resource = selectedResource;
        if (resource) {
            onResourceHideToggle(resource, !resource.hidden);
        }
    };

    const markResourceAsShared = async () => {
        setLoading(true);
        try {
            const resource = selectedResource;
            if (resource) {
                const serversToUpdate = resource.type === 'server' ? [resource.resourceId] : [];
                const databasesToUpdate = resource.type === 'database' ? [resource.resourceId] : [];
                await applicationDependencyService.updateSharedInfrastructure({
                    serverIds: serversToUpdate,
                    databaseIds: databasesToUpdate,
                    isSharedInfrastructure: '1',
                    portfolioId,
                });
                await applicationGroupingService.createGroups(portfolioId);
                history.push(`/portfolio/${portfolioId}/planning/grouping`);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_MARKING_AS_SHARED_INFRA);
        } finally {
            setLoading(false);
        }
    };

    const beginRemoveApplication = () => {
        if (dependencies && selectedResource) {
            const serverDependencies: Record<string, boolean> = {};
            const databaseDependencies: Record<string, boolean> = {};

            const filteredDependencies = dependencies.filter(dependency => dependency.srcAppId === selectedResource.resourceId &&
                (dependency.resourceType === 'applications-servers-v0' || dependency.resourceType === 'database-applications-v0') &&
                dependency.status === DEPENDENCY_STATUS.ACTIVE && !dependency.isSharedInfrastructure);
            filteredDependencies.forEach(dependency => {
                const { resourceId, resourceType } = dependency;
                if (resourceType === 'applications-servers-v0') {
                    serverDependencies[resourceId] = true; 
                } else if (resourceType === 'database-applications-v0') {
                    databaseDependencies[resourceId] = true;
                }
            });
            setServerDependenciesToManage(serverDependencies);
            setDatabaseDependenciesToManage(databaseDependencies);
        }
        setRemoveAppVisible(true);
    };

    const resourceActions = (resource: DependencyDetail): JSX.Element => {
        if (resource.type === 'application') {
            return (
                <SpaceBetween size='l' direction='horizontal'>
                    <Button onClick={toggleResourceHide}>{resource.hidden ? constants.UNHIDE_BUTTON_TEXT : constants.HIDE_BUTTON_TEXT }</Button>
                    <Button onClick={beginRemoveApplication} variant='primary'>{constants.REMOVE_BUTTON_TEXT}</Button>
                </SpaceBetween>
            );
        } else {
            return (
                <SpaceBetween size='l' direction='horizontal'>
                    <Button onClick={toggleResourceHide}>{resource.hidden ? constants.UNHIDE_BUTTON_TEXT : constants.HIDE_BUTTON_TEXT }</Button>
                    <Button loading={loading} onClick={markResourceAsShared} variant='primary'>{constants.MARK_AS_SHARED_BUTTON_TEXT}</Button>
                </SpaceBetween>
            );
        }
    };

    const getResourceToggles = (resourceType: 'server' | 'database'): JSX.Element => {
        const resources = resourceType === 'server' ? serverDependenciesToManage : databaseDependenciesToManage;

        const onToggleChange = (resourceId: string, resourceType: 'server' | 'database') =>
            ({ detail }: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
                if (resourceType === 'server') {
                    serverDependenciesToManage[resourceId] = detail.checked;
                    setServerDependenciesToManage(deepCopy(serverDependenciesToManage));
                } else {
                    databaseDependenciesToManage[resourceId] = detail.checked;
                    setDatabaseDependenciesToManage(deepCopy(databaseDependenciesToManage));
                }
            };
        
        const resourceIds = Object.keys(resources);
        return resourceIds.length ? (
            <Box padding={{horizontal: 'm'}}>
                {resourceIds.map(resourceId => (
                    <Box key={resourceId}>
                        <Box display='inline-block'>{resourceId}</Box>
                        <Box display='inline-block' float='right'>
                            <Toggle checked={resources[resourceId]} onChange={onToggleChange(resourceId, resourceType)}/>
                        </Box>
                    </Box>
                ))}
            </Box>
        ) : (
            <Alert>{resourceType === 'server' ? constants.NO_SERVER_DEPENDENCIES : constants.NO_DATABASE_DEPENDENCIES}</Alert>
        );
    };

    const onConfirmRemoveApplication = async () => {
        setLoading(true);
        try {
            if (dependencies && selectedResource && groupDetail) {
                const stage = 'confirm';
                const otherDependenciesToUpdate: GeneralDependencyType[] = [];
                const appServerMappingsToUpdate: AppServerMappingToUpdate[] = [];
                const appDatabaseMappingsToUpdate: AppDatabaseMappingToUpdate[] = [];

                const unselectedApplications = groupDetail.applications.filter(application => application.applicationId !== selectedResource.resourceId);

                unselectedApplications.forEach(unselectedApp => {
                    otherDependenciesToUpdate.push({
                        srcAppId: selectedResource.resourceId,
                        destAppId: unselectedApp.applicationId,
                        resourceType: 'user-import'
                    }, {
                        srcAppId: selectedResource.resourceId,
                        destAppId: unselectedApp.applicationId,
                        resourceType: 'server-communications-v0',

                    }, {
                        srcAppId: unselectedApp.applicationId,
                        destAppId: selectedResource.resourceId,
                        resourceType: 'user-import'
                    }, {
                        srcAppId: unselectedApp.applicationId,
                        destAppId: selectedResource.resourceId,
                        resourceType: 'server-communications-v0',

                    });
                });

                const filteredDependencies = dependencies.filter(dependency => dependency.srcAppId === selectedResource.resourceId &&
                    (dependency.resourceType === 'applications-servers-v0' || dependency.resourceType === 'database-applications-v0') &&
                    dependency.status === DEPENDENCY_STATUS.ACTIVE && !dependency.isSharedInfrastructure);
                filteredDependencies.forEach(dependency => {
                    const { resourceId, srcAppId, destAppId, resourceType } = dependency;
                    if (resourceType === 'applications-servers-v0') {
                        const toggle = serverDependenciesToManage[resourceId];
                        const srcAppServerMapping = findApplicationServerMapping(appServerMappings, srcAppId, resourceId);
                        const destAppServerMapping = findApplicationServerMapping(appServerMappings, destAppId, resourceId);
                        // skip the pair of applications if any one of them doesn't have mapping with current server
                        if (!srcAppServerMapping || !destAppServerMapping) {
                            return;
                        } else {
                            if (statusDiff(srcAppServerMapping.statusInGrouping, toggle)) {
                                appServerMappingsToUpdate.push({
                                    applicationId: srcAppId,
                                    serverId: resourceId,
                                    status: decideDetailedMappingStatus(serverDependenciesToManage[resourceId], stage),
                                });
                            }
                            if (statusDiff(destAppServerMapping.statusInGrouping, !toggle)) {
                                appServerMappingsToUpdate.push({
                                    applicationId: destAppId,
                                    serverId: resourceId,
                                    status: decideDetailedMappingStatus(!toggle, stage),
                                });
                            }
                        }
                    } else if (resourceType === 'database-applications-v0') {
                        const toggle = databaseDependenciesToManage[resourceId];
                        const srcAppDatabaseMapping = findApplicationDatabaseMapping(appDatabaseMappings, srcAppId, resourceId);
                        const destAppDatabaseMapping = findApplicationDatabaseMapping(appDatabaseMappings, destAppId, resourceId);
                        // skip the pair of applications if any one of them doesn't have mapping with current database
                        if (!srcAppDatabaseMapping || !destAppDatabaseMapping) {
                            return;
                        } else {
                            if (statusDiff(srcAppDatabaseMapping.statusInGrouping, toggle)) {
                                appDatabaseMappingsToUpdate.push({
                                    applicationId: srcAppId,
                                    databaseId: resourceId,
                                    status: decideDetailedMappingStatus(databaseDependenciesToManage[resourceId], stage),
                                });
                            }
                            if (statusDiff(destAppDatabaseMapping.statusInGrouping, !toggle)) {
                                appDatabaseMappingsToUpdate.push({
                                    applicationId: destAppId,
                                    databaseId: resourceId,
                                    status: decideDetailedMappingStatus(!toggle, stage),
                                });
                            }
                        }
                    }
                });

                if (appServerMappingsToUpdate?.length > 0 || appDatabaseMappingsToUpdate?.length > 0) {
                    await Promise.all([
                        changeAppServerMappingStatus(appServerMappingsToUpdate, portfolioId, applicationServerService),
                        changeAppDatabaseMappingStatus(appDatabaseMappingsToUpdate, portfolioId, databaseApplicationService),
                    ]);
                }
                await changeDependencyStatus(DEPENDENCY_STATUS.INACTIVE, portfolioId, otherDependenciesToUpdate, applicationDependencyService);
                await applicationGroupingService.createGroups(portfolioId);
                history.push(`/portfolio/${portfolioId}/planning/grouping`);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_REMOVING_APPLICATION);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const buildResourceDetail = (resource: DependencyDetail, group: ApplicationGroup): JSX.Element => {
        return removeAppVisible ? (
            <Box>
                {header(constants.REMOVE_APPLICATON)}
                <div className='remove-application-side-panel'>
                    <Box padding={{horizontal: 'l'}}>
                        <p>{constants.APPLICATION} <b>{resource.resourceId}</b> {constants.REMOVE_APPLICATION_MESSAGE_1} {group.name}
                            {constants.REMOVE_APPLICATION_MESSAGE_2}</p>
                        <h4>{constants.SERVER_DEPENDENCIES}:</h4>
                        {getResourceToggles('server')}
                        <h4>{constants.DATABASE_DEPENDENCIES}:</h4>
                        {(getResourceToggles('database'))}
                    </Box>
                </div>
                <Box padding={{vertical: 'm', horizontal: 'l'}} className='grouping-resource-action-panel'>
                    <SpaceBetween size='l' direction='horizontal'>
                        <Button onClick={() => setRemoveAppVisible(false)} disabled={loading}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant='primary' loading={loading} onClick={onConfirmRemoveApplication}>{constants.CONFIRM_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                </Box>
            </Box>
        ) : (
            <Box>
                {header(resource.resourceId)}
                <Box padding={{left: 'l'}}>
                    <ExpandableSection variant='footer' defaultExpanded header={constants.APPLICATION_S}>
                        <Box padding={{horizontal: 'l'}}>{Array.from(resource.applications).join(', ')}</Box>
                    </ExpandableSection>
                    <ExpandableSection variant='footer' defaultExpanded header={constants.SERVER_S}>
                        <Box padding={{left: 'l'}}>
                            {Array.from(resource.servers).join(', ')}
                        </Box>
                    </ExpandableSection>
                    <ExpandableSection variant='footer' defaultExpanded header={constants.DATABASE_S}>
                        <Box padding={{left: 'l'}}>
                            {Array.from(resource.databases).join(', ')}
                        </Box>
                    </ExpandableSection>
                    <Box padding={{vertical: 'm'}} className='grouping-resource-action-panel'>{resourceActions(resource)}</Box>
                </Box>
            </Box>
        );
    };
    
    return groupDetail ? (
        <Box className='group-summary' display='inline-block' float='right'>
            {selectedResource ? buildResourceDetail(selectedResource, groupDetail) : buildGroupDetail(groupDetail)}
        </Box>
    ) : null;
}

export default withFlashContext(ApplicationGroupDependencyGraphResourceDetail);
